import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { AUTHENTICATE } from "../../gql/auth";
import { VERIFY_TRAINER } from "../gql/trainerPortal";
import * as jose from "jose";
import { useNavigate } from "react-router-dom";

import { TextField, Alert } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import LoadingButton from "@mui/lab/LoadingButton";
import Typography from "@mui/material/Typography";
import logo from "../../static/PPlay_logo.png";
import "../../style/App.css";

const TrainerLogin = () => {
  const [values, setValues] = useState({
    phone: "",
    password: "",
    showPassword: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    phone: "",
    password: "",
  });
  const [showAlert, setShowAlert] = useState({ message: "", isError: false });
  const navigate = useNavigate();

  useEffect(() => {
    const trainerToken = window.localStorage.getItem("trainerPortalToken");
    if (trainerToken) {
      navigate("/affiliatorportal/dashboard");
    }
  }, [navigate]);

  const [postLogin] = useMutation(AUTHENTICATE, {
    onError: (error) => {
      console.log("Login error:", error);
      setShowAlert({ message: "Error on server", isError: true });
      setLoading(false);
      setTimeout(() => {
        setShowAlert({ message: "", isError: false });
      }, 3000);
    },
    onCompleted: async (result) => {
      if (result.LogIn.error) {
        setShowAlert({ message: result.LogIn.message || "Login failed", isError: true });
        setLoading(false);
        setTimeout(() => {
          setShowAlert({ message: "", isError: false });
        }, 3000);
        return;
      }

      try {
        const decodedToken = jose.decodeJwt(result.LogIn.accessToken);
        const userId = decodedToken.user_id;

        // Verify user has trainer affiliation
        // We'll verify this in the component that uses the query
        const data = JSON.stringify({
          token: result.LogIn.accessToken,
          userID: userId,
        });

        window.localStorage.setItem("trainerPortalToken", data);
      navigate("/affiliatorportal/dashboard");
      } catch (error) {
        console.error("Token decode error:", error);
        setShowAlert({ message: "Invalid token", isError: true });
        setLoading(false);
      }
    },
  });

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
    // Clear error when user types
    if (errors[prop]) {
      setErrors({ ...errors, [prop]: "" });
    }
  };

  const handleClick = async () => {
    setErrors({
      phone: "",
      password: "",
    });
    setLoading(true);
    let errorExist = false;
    const tempErrors = {};

    if (!values.phone) {
      tempErrors.phone = "Phone number is required.";
      errorExist = true;
    }
    if (!values.password) {
      tempErrors.password = "Password is required.";
      errorExist = true;
    }

    if (errorExist) {
      setErrors(tempErrors);
      setLoading(false);
      return;
    }

    postLogin({
      variables: { phone: values.phone, password: values.password },
    });
  };

  return (
    <>
      <CssBaseline />
      <Container maxWidth="sm">
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            bgcolor: "rgb(199, 221, 233)",
            color: "#000",
          }}
        >
          <Box>
            <FormControl
              sx={{
                p: 1.4,
                width: "280px",
                height: "60px",
                borderRadius: "40px",
                m: 2,
                boxShadow: 10,
              }}
            >
              <img alt="PowerPlay" src={logo} width="250px" height="50px" />
            </FormControl>
            <Typography variant="h4" fontWeight="bold" component="p">
              Affiliate Dashboard
            </Typography>
            <Typography variant="subtitle1" component="p" sx={{ mb: 2 }}>
              Enter your credentials to continue
            </Typography>
          </Box>
          <Box>
            <FormControl
              style={{ borderRadius: "3px" }}
              sx={{ m: 2, width: "300px", backgroundColor: "#fff" }}
            >
              <TextField
                id="phone"
                label="Phone Number"
                size="large"
                variant="filled"
                value={values.phone}
                onChange={handleChange("phone")}
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </FormControl>
            <FormControl
              style={{ borderRadius: "3px" }}
              sx={{ m: 2, width: "300px", backgroundColor: "#fff" }}
            >
              <TextField
                id="password"
                size="large"
                variant="filled"
                type={values.showPassword ? "text" : "password"}
                value={values.password}
                onChange={handleChange("password")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {values.showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                label="Password"
                error={!!errors.password}
                helperText={errors.password}
              />
            </FormControl>
            <FormControl sx={{ m: 4, width: "300px", fontWeight: "bold" }}>
              <LoadingButton
                onClick={handleClick}
                loading={loading}
                size="large"
                variant="contained"
                sx={{ backgroundColor: "#000", height: 55 }}
              >
                Sign In
              </LoadingButton>
            </FormControl>
          </Box>
        </Box>
        {showAlert.message && !showAlert.isError && (
          <Alert
            sx={{ position: "absolute", bottom: "1em", right: "1em" }}
            severity="success"
          >
            {showAlert.message}
          </Alert>
        )}
        {showAlert.message && showAlert.isError && (
          <Alert
            sx={{ position: "absolute", bottom: "1em", right: "1em" }}
            severity="error"
          >
            {showAlert.message}
          </Alert>
        )}
      </Container>
    </>
  );
};

export default TrainerLogin;
